import { devLog, devError } from '../utils/devLogger';
/**
 * Program Assignment Service
 *
 * Manages assignment of facilitators to programs and related operations.
 * Provides functionality for administrators to assign facilitators to programs
 * and for facilitators to view their assigned programs.
 */

import { auth, db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import auditTrailService from './auditTrailService';

class ProgramAssignmentService {
  constructor() {
    this.currentUser = null;
    this.userProfile = null;
  }

  /**
   * Initialize service with current user context
   */
  async initialize() {
    this.currentUser = auth.currentUser;
    if (this.currentUser) {
      const userDoc = await getDoc(doc(db, 'users', this.currentUser.uid));
      this.userProfile = userDoc.data();
    }
  }

  /**
   * Assign facilitator to programs (Admin only)
   * @param {string} facilitatorId - The facilitator's user ID
   * @param {Array} programIds - Array of program IDs to assign
   */
  async assignFacilitatorToPrograms(facilitatorId, programIds) {
    await this.initialize();

    // Only admins can assign programs
    if (this.userProfile?.role !== 'admin') {
      throw new Error('Only administrators can assign programs to facilitators');
    }

    try {
      // Get facilitator's current data
      const facilitatorDoc = await getDoc(doc(db, 'users', facilitatorId));
      const facilitatorData = facilitatorDoc.data();

      if (!facilitatorData) {
        throw new Error('Facilitator not found');
      }

      if (!['facilitator', 'admin'].includes(facilitatorData.role)) {
        throw new Error('Only facilitators and administrators can be assigned to programmes');
      }

      // Update facilitator's assigned programs
      await updateDoc(doc(db, 'users', facilitatorId), {
        assignedPrograms: programIds,
        programsAssignedBy: this.currentUser.uid,
        programsAssignedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update each program with the assigned facilitator
      for (const programId of programIds) {
        const programDoc = await getDoc(doc(db, 'programs', programId));
        const programData = programDoc.data();

        if (programData) {
          const currentFacilitators = programData.assignedFacilitators || [];
          if (!currentFacilitators.includes(facilitatorId)) {
            await updateDoc(doc(db, 'programs', programId), {
              assignedFacilitators: arrayUnion(facilitatorId),
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      // Normalize previous programs (admins may only have programId, not assignedPrograms)
      const previousPrograms =
        Array.isArray(facilitatorData.assignedPrograms) && facilitatorData.assignedPrograms.length > 0
          ? facilitatorData.assignedPrograms
          : facilitatorData.programId
          ? [facilitatorData.programId]
          : [];
      const programsToRemove = previousPrograms.filter(id => !programIds.includes(id));

      for (const programId of programsToRemove) {
        await updateDoc(doc(db, 'programs', programId), {
          assignedFacilitators: arrayRemove(facilitatorId),
          updatedAt: serverTimestamp()
        });
      }

      // Log the assignment
      await auditTrailService.logAction('facilitator.programs.assigned', {
        facilitatorId: facilitatorId,
        assignedPrograms: programIds,
        previousPrograms: previousPrograms,
        assignedBy: this.currentUser.uid,
        assignmentTimestamp: new Date().toISOString()
      });

      return { success: true, assignedPrograms: programIds };
    } catch (error) {
      devError('Error assigning programs to facilitator:', error);
      throw new Error('Failed to assign programs: ' + error.message);
    }
  }

  /**
   * Get all available programs for assignment
   */
  async getAvailablePrograms() {
    try {
      const programsQuery = query(
        collection(db, 'programs'),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(programsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      devError('Error fetching available programs:', error);
      throw new Error('Failed to fetch programs');
    }
  }

  /**
   * Get facilitator's assigned programs
   * @param {string} facilitatorId - Optional facilitator ID (defaults to current user)
   */
  async getFacilitatorPrograms(facilitatorId = null) {
    await this.initialize();

    const targetId = facilitatorId || this.currentUser?.uid;
    if (!targetId) {
      throw new Error('No user specified');
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', targetId));
      const userData = userDoc.data();

      if (!userData) {
        throw new Error('User not found');
      }

      devLog('User data for facilitator programs:', userData);
      // Normalize: admins may only have programId (string) instead of assignedPrograms (array)
      const assignedProgramIds =
        Array.isArray(userData.assignedPrograms) && userData.assignedPrograms.length > 0
          ? userData.assignedPrograms
          : userData.programId
          ? [userData.programId]
          : [];
      devLog('Assigned program IDs:', assignedProgramIds);

      if (assignedProgramIds.length === 0) {
        return [];
      }

      // Fetch program details
      const programs = [];
      for (const programId of assignedProgramIds) {
        const programDoc = await getDoc(doc(db, 'programs', programId));
        if (programDoc.exists()) {
          programs.push({ id: programDoc.id, ...programDoc.data() });
        }
      }

      return programs;
    } catch (error) {
      devError('Error fetching facilitator programs:', error);
      throw new Error('Failed to fetch assigned programs');
    }
  }

  /**
   * Get all facilitators with their program assignments
   */
  async getFacilitatorsWithPrograms() {
    await this.initialize();

    if (this.userProfile?.role !== 'admin') {
      throw new Error('Only administrators can view all facilitator assignments');
    }

    try {
      // Include both facilitators and admins so admins can also be assigned to programmes
      const [facilitatorSnap, adminSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('role', '==', 'facilitator'))),
        getDocs(query(collection(db, 'users'), where('role', '==', 'admin'))),
      ]);

      const allDocs = [...facilitatorSnap.docs, ...adminSnap.docs];
      const facilitators = [];
      for (const docSnap of allDocs) {
        const facilitatorData = { id: docSnap.id, ...docSnap.data() };

        // Normalize: admins may only have programId (string) instead of assignedPrograms (array)
        const assignedProgramIds =
          Array.isArray(facilitatorData.assignedPrograms) && facilitatorData.assignedPrograms.length > 0
            ? facilitatorData.assignedPrograms
            : facilitatorData.programId
            ? [facilitatorData.programId]
            : [];

        const assignedProgramsDetails = [];
        for (const programId of assignedProgramIds) {
          const programDoc = await getDoc(doc(db, 'programs', programId));
          if (programDoc.exists()) {
            assignedProgramsDetails.push({ id: programDoc.id, ...programDoc.data() });
          }
        }

        facilitators.push({
          ...facilitatorData,
          assignedPrograms: assignedProgramIds,
          assignedProgramsDetails,
        });
      }

      return facilitators;
    } catch (error) {
      devError('Error fetching facilitators with programs:', error);
      throw new Error('Failed to fetch facilitators');
    }
  }

  /**
   * Remove facilitator from specific programs
   * @param {string} facilitatorId - The facilitator's user ID
   * @param {Array} programIds - Array of program IDs to remove
   */
  async removeFacilitatorFromPrograms(facilitatorId, programIds) {
    await this.initialize();

    if (this.userProfile?.role !== 'admin') {
      throw new Error('Only administrators can modify program assignments');
    }

    try {
      const facilitatorDoc = await getDoc(doc(db, 'users', facilitatorId));
      const facilitatorData = facilitatorDoc.data();

      if (!facilitatorData) {
        throw new Error('Facilitator not found');
      }

      const currentPrograms = facilitatorData.assignedPrograms || [];
      const updatedPrograms = currentPrograms.filter(id => !programIds.includes(id));

      // Update facilitator's assigned programs
      await updateDoc(doc(db, 'users', facilitatorId), {
        assignedPrograms: updatedPrograms,
        updatedAt: serverTimestamp()
      });

      // Remove facilitator from programs
      for (const programId of programIds) {
        await updateDoc(doc(db, 'programs', programId), {
          assignedFacilitators: arrayRemove(facilitatorId),
          updatedAt: serverTimestamp()
        });
      }

      // Log the removal
      await auditTrailService.logAction('facilitator.programs.removed', {
        facilitatorId: facilitatorId,
        removedPrograms: programIds,
        removedBy: this.currentUser.uid,
        removalTimestamp: new Date().toISOString()
      });

      return { success: true, removedPrograms: programIds };
    } catch (error) {
      devError('Error removing facilitator from programs:', error);
      throw new Error('Failed to remove programs: ' + error.message);
    }
  }

  /**
   * Check if facilitator can access specific program
   * @param {string} programId - The program ID to check
   * @param {string} facilitatorId - Optional facilitator ID (defaults to current user)
   */
  async canAccessProgram(programId, facilitatorId = null) {
    await this.initialize();

    const targetId = facilitatorId || this.currentUser?.uid;
    const userRole = facilitatorId ?
      (await getDoc(doc(db, 'users', facilitatorId))).data()?.role :
      this.userProfile?.role;

    // Admins can access all programs
    if (userRole === 'admin') {
      return true;
    }

    // Check if facilitator is assigned to the program
    if (userRole === 'facilitator') {
      const assignedPrograms = await this.getFacilitatorPrograms(targetId);
      return assignedPrograms.some(program => program.id === programId);
    }

    return false;
  }

  /**
   * Get program statistics for facilitator
   * @param {string} programId - The program ID
   */
  async getProgramStats(programId) {
    await this.initialize();

    if (!await this.canAccessProgram(programId)) {
      throw new Error('Access denied to this program');
    }

    try {
      // Get modules in this program
      const modulesQuery = query(
        collection(db, 'modules'),
        where('programId', '==', programId)
      );
      const modulesSnapshot = await getDocs(modulesQuery);
      const modules = modulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Get cohorts in this program
      const cohortsQuery = query(
        collection(db, 'cohorts'),
        where('programId', '==', programId)
      );
      const cohortsSnapshot = await getDocs(cohortsQuery);
      const cohorts = cohortsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate learner count across all cohorts
      const totalLearners = cohorts.reduce((sum, cohort) => sum + (cohort.enrolledCount || 0), 0);

      return {
        totalModules: modules.length,
        totalCohorts: cohorts.length,
        totalLearners: totalLearners,
        activeModules: modules.filter(m => m.status === 'active').length,
        activeCohorts: cohorts.filter(c => c.status === 'active').length,
        modules: modules,
        cohorts: cohorts
      };
    } catch (error) {
      devError('Error fetching program stats:', error);
      throw new Error('Failed to fetch program statistics');
    }
  }
}

// Create and export singleton instance
const programAssignmentService = new ProgramAssignmentService();
export default programAssignmentService;